import { keysToCamelCase, keysToSnakeCase } from '@hotjar/webapp/lib/convert-string';
import type { ProductCatalog as V3ProductCatalog } from '@hotjar/product-catalog';
import { legacyPlanIdToV3Sku, planDictionary } from '~/product-catalog/constants/map-dictionary';
import type { ProductCatalog, ProductCatalogDto } from './types';
import { ProductType } from './types';
import type { FeatureGroup } from '../../revenue/plan/types';

export const mapProductCatalogtModelToDto = (catalog: ProductCatalog): ProductCatalogDto =>
  keysToSnakeCase(catalog);

export const mapProductCatalogtDtoToModel = (catalog: ProductCatalogDto): ProductCatalog =>
  keysToCamelCase(catalog);

export const mapV3ProductCatalogToLegacy = (v3ProductCatalog: V3ProductCatalog): ProductCatalog => {
  const ask = v3ProductCatalog.find((product) => product.id === 'PROD-1');
  const observe = v3ProductCatalog.find((product) => product.id === 'PROD-2');
  const v3SkuToLegacyPlanId = Object.fromEntries(
    Object.entries(legacyPlanIdToV3Sku).map((a) => a.reverse()),
  );

  if (!ask || !observe) {
    throw new Error('mapV3ProductCatalogToLegacy: should return an ask or observe product');
  }

  const mappedAskProducts = ask.plans.map((plan) => {
    const tier = planDictionary[plan.id] as FeatureGroup;

    return plan.allowances.map((allowance) => ({
      sku: v3SkuToLegacyPlanId[allowance.sku],
      type: ProductType.Feedback as ProductType.Feedback,
      monthlyResponses: 'monthlyResponses' in allowance ? allowance.monthlyResponses : 0,
      tier,
      selfServeEnabled: allowance.selfServeEnabled,
    }));
  });

  const mappedObserveProducts = observe.plans.map((plan) => {
    const tier = planDictionary[plan.id] as FeatureGroup;

    return plan.allowances.map((allowance) => ({
      sku: v3SkuToLegacyPlanId[allowance.sku],
      dailyCcSessions: 'dailyCcSessions' in allowance ? allowance.dailyCcSessions : 0,
      type: ProductType.Analytics as ProductType.Analytics,
      tier,
      dailySessions: 'dailySessions' in allowance ? allowance.dailySessions : 0,
      selfServeEnabled: allowance.selfServeEnabled,
    }));
  });

  const result = {
    analyticsProducts: mappedObserveProducts.flat(),
    feedbackProducts: mappedAskProducts.flat(),
  };

  return result;
};
